import axiosInstance, { ApiException } from "./axiosInstance";
import { AxiosRequestConfig, AxiosResponse } from "axios";
import { ApiResponse, RequestConfig } from "@/services/types/common.types";
import { API_BASE_URL } from "@/config/env";

/**
 * Extended request config with custom options
 */
interface ExtendedRequestConfig extends AxiosRequestConfig, RequestConfig {}

function resolveBaseUrl(config?: ExtendedRequestConfig): string | undefined {
  return config?.baseURL ?? API_BASE_URL;
}

/**
 * API Client
 * Provides helper methods for making API calls (GET, POST, PUT, DELETE, PATCH)
 */

class ApiClient {
  /**
   * Generic request method
   */
  private async request<T = unknown>(
    method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH",
    url: string,
    data?: unknown,
    config?: ExtendedRequestConfig,
  ): Promise<ApiResponse<T>> {
    try {
      const requestConfig: ExtendedRequestConfig = {
        method,
        url,
        baseURL: resolveBaseUrl(config),
        ...config,
      };

      if (
        data &&
        (method === "POST" || method === "PUT" || method === "PATCH" || method === "DELETE")
      ) {
        requestConfig.data = data;
      }

      if (data && method === "GET") {
        requestConfig.params = data;
      }

      const response: AxiosResponse<ApiResponse<T>> =
        await axiosInstance.request(requestConfig);

      // Return the response data directly if it's already in ApiResponse format
      if (response.data && typeof response.data === "object") {
        return response.data;
      }

      // Otherwise, wrap it in ApiResponse format
      return {
        success: true,
        data: response.data as T,
      };
    } catch (error) {
      if (error instanceof ApiException) {
        throw error;
      }
      throw new ApiException(
        error instanceof Error ? error.message : "An unexpected error occurred",
        500,
      );
    }
  }

  /**
   * GET request
   */
  async get<T = unknown>(
    url: string,
    params?: Record<string, unknown>,
    config?: ExtendedRequestConfig,
  ): Promise<ApiResponse<T>> {
    return this.request<T>("GET", url, params, config);
  }

  /**
   * POST request
   */
  async post<T = unknown>(
    url: string,
    data?: unknown,
    config?: ExtendedRequestConfig,
  ): Promise<ApiResponse<T>> {
    return this.request<T>("POST", url, data, config);
  }

  /**
   * PUT request
   */
  async put<T = unknown>(
    url: string,
    data?: unknown,
    config?: ExtendedRequestConfig,
  ): Promise<ApiResponse<T>> {
    return this.request<T>("PUT", url, data, config);
  }

  /**
   * PATCH request
   */
  async patch<T = unknown>(
    url: string,
    data?: unknown,
    config?: ExtendedRequestConfig,
  ): Promise<ApiResponse<T>> {
    return this.request<T>("PATCH", url, data, config);
  }

  /**
   * DELETE request
   */
  async delete<T = unknown>(
    url: string,
    data?: unknown,
    config?: ExtendedRequestConfig,
  ): Promise<ApiResponse<T>> {
    return this.request<T>("DELETE", url, data, config);
  }

  /**
   * POST multipart/form-data (e.g. file uploads). Do not set Content-Type manually.
   */
  async postFormData<T = unknown>(
    url: string,
    formData: FormData,
    config?: ExtendedRequestConfig,
  ): Promise<AxiosResponse<T>> {
    const formDataConfig: ExtendedRequestConfig = {
      ...config,
      baseURL: resolveBaseUrl(config),
      headers: {
        ...config?.headers,
        "Content-Type": undefined,
      },
    };
    return axiosInstance.post<T>(url, formData, formDataConfig);
  }

  /**
   * Upload file(s)
   */
  async upload<T = unknown>(
    url: string,
    formData: FormData,
    config?: ExtendedRequestConfig,
  ): Promise<ApiResponse<T>> {
    return this.post<T>(url, formData, {
      ...config,
      headers: {
        "Content-Type": "multipart/form-data",
        ...config?.headers,
      },
    });
  }

  /**
   * Download file
   */
  async download(
    url: string,
    params?: Record<string, unknown>,
    config?: ExtendedRequestConfig,
  ): Promise<Blob> {
    try {
      const response = await axiosInstance.get(url, {
        params,
        responseType: "blob",
        ...config,
      });
      return response.data;
    } catch (error) {
      if (error instanceof ApiException) {
        throw error;
      }
      throw new ApiException(
        error instanceof Error ? error.message : "Download failed",
        500,
      );
    }
  }
}

// Create and export singleton instance
const apiClient = new ApiClient();
export default apiClient;
export { apiClient as ApiClient };

// Export individual methods for convenience
export const {
  get,
  post,
  put,
  patch,
  delete: del,
  upload,
  download,
  postFormData,
} = apiClient;
